import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, configureAuth } from "../api/client";

const STORAGE_KEY = "occ.session.v1";

export interface Profile {
  firstName?: string;
  lastName?: string;
  displayName: string;
  email?: string;
  phone?: string;
  status?: string;
}

export interface Session {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  userId: string;
  agencyId: string;
  role: string;
  employeeId: string;
  profile?: Profile;
}

interface AuthCtx {
  session: Session | null;
  signIn: (s: Session) => void;
  signOut: () => void;
  /** Exchanges the stored refresh token for a new access token. Exposed so
   *  callers outside the normal fetch path (e.g. an SSE stream whose
   *  EventSource errored, possibly on an expired token) can proactively
   *  refresh instead of waiting on the next 401'd request to do it. */
  refreshAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthCtx | undefined>(undefined);

function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    // An expired ACCESS token is not a dead session — the refresh token
    // outlives it by weeks. Discarding the whole session here is what sent a
    // dispatcher back to the login screen on every reload after the first
    // hour. Keep it; the first 401 refreshes, and only a refusal signs out.
    if (parsed.expiresAt && parsed.expiresAt < Date.now() && !parsed.refreshToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(loadSession);
  const sessionRef = useRef<Session | null>(session);

  const signIn = useCallback((s: Session) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    sessionRef.current = s;
    setSession(s);
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    sessionRef.current = null;
    setSession(null);
  }, []);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Exchanges the stored refresh token for a new access token, updating the
  // persisted session so a later reload restores the renewed one. Returns null
  // when the refresh token itself is dead, which is the only case that should
  // land the dispatcher back on the login screen.
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const current = sessionRef.current;
    if (!current?.refreshToken) return null;
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || "/api/v1"}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: current.refreshToken }),
      });
      if (!res.ok) return null;
      const body = await res.json();
      if (!body?.access_token) return null;
      const next: Session = {
        ...current,
        accessToken: body.access_token,
        refreshToken: body.refresh_token ?? current.refreshToken,
        expiresAt: Date.now() + (body.expires_in ?? 3600) * 1000,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      sessionRef.current = next;
      setSession(next);
      return next.accessToken;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    configureAuth({
      getAccessToken: () => sessionRef.current?.accessToken ?? null,
      onUnauthorized: signOut,
      refreshAccessToken,
    });
  }, [signOut, refreshAccessToken]);

  // Fetch /auth/me whenever the session lacks a profile (post-login, or
  // post-reload before the first profile fetch landed).
  useEffect(() => {
    if (!session || session.profile) return;
    let cancelled = false;
    api.me().then((me) => {
      if (cancelled) return;
      const next: Session = {
        ...session,
        profile: {
          firstName: me.first_name,
          lastName: me.last_name,
          displayName: me.display_name,
          email: me.email,
          phone: me.phone,
          status: me.status,
        },
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      sessionRef.current = next;
      setSession(next);
    }).catch(() => {/* leave profile undefined; Layout falls back to employeeId */});
    return () => { cancelled = true; };
  }, [session?.accessToken, session?.profile]);

  const value = useMemo(
    () => ({ session, signIn, signOut, refreshAccessToken }),
    [session, signIn, signOut, refreshAccessToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
